import { type ErrorInfo, type ReactNode, Component, useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { ApiError } from '@/lib/errors/api';
import { JupiterApiError } from '@/lib/errors/jupiter';
import { reportError, shouldIgnore } from '@/lib/errors/report';
import { tokenFormatter } from '@/lib/formatting';
import { logger } from '@/lib/logger';

import { useTokenResult } from '@/hooks/tokenQueries/useTokenResult';
import { CheckoutModes, useCheckoutMode } from '@/hooks/useCheckoutMode';

import type { ChainAddress } from '@/utils/models';

import { LimitOrderToast } from '../TransactionToast/LimitOrderToast';

export type ErrorBoundaryProps = {
  children: ReactNode;
  sellTokenChainId?: number;
  sellTokenAddress?: ChainAddress;
  buyTokenChainId?: number;
  buyTokenAddress?: ChainAddress;
};

type TransactionModuleErrorBoundaryProps = ErrorBoundaryProps & {
  sellInputFormatted: string;
  buyInputFormatted: string;
  resetCheckout: () => void;
};

type ErrorBoundaryState = {
  error?: Error;
  hasBeenHandled?: boolean;
};

function ErrorHandler({
  error,
  onHandled,
  sellTokenChainId,
  sellTokenAddress,
  buyTokenChainId,
  buyTokenAddress,
  sellInputFormatted,
  buyInputFormatted,
  resetCheckout,
}: Omit<TransactionModuleErrorBoundaryProps, 'children'> & {
  error: Error;
  onHandled: () => void;
}) {
  const [checkoutMode] = useCheckoutMode();
  const hasShownToastRef = useRef(false);
  const { token: sellToken } = useTokenResult(sellTokenAddress, sellTokenChainId);
  const { token: buyToken } = useTokenResult(buyTokenAddress, buyTokenChainId);
  const sellTokenSymbol = sellToken?.symbol || '';
  const buyTokenSymbol = buyToken?.symbol || '';

  useEffect(() => {
    if (hasShownToastRef.current) return;
    hasShownToastRef.current = true;

    const formattedSellAmount = tokenFormatter.format(sellInputFormatted);
    const formattedBuyAmount = tokenFormatter.format(buyInputFormatted);
    const sellTokenInfo = {
      address: sellTokenAddress || ('' as ChainAddress),
      chainId: sellTokenChainId || 1,
      symbol: sellTokenSymbol,
    };
    const buyTokenInfo = {
      address: buyTokenAddress || ('' as ChainAddress),
      chainId: buyTokenChainId || 1,
      symbol: buyTokenSymbol,
    };

    let toastId: string | number;
    const onClose = () => toast.dismiss(toastId);
    const options = { duration: 6000, unstyled: true } as const;

    if (checkoutMode === CheckoutModes.advanced) {
      toastId = toast(
        <LimitOrderToast
          checkoutStep="userCancelled"
          sellAmount={formattedSellAmount}
          buyAmount={formattedBuyAmount}
          sellToken={sellTokenInfo}
          buyToken={buyTokenInfo}
          onClose={onClose}
        />,
        options,
      );
    } else {
      toast.error('Submission failed', {
        description: `${formattedSellAmount} ${sellTokenSymbol} to ${formattedBuyAmount} ${buyTokenSymbol}`,
        duration: 6000,
        className:
          '!bg-special-background-modal !border !border-token-gray-300 !text-token-gray-800 !rounded-xl',
      });
    }

    resetCheckout();
    onHandled();
  }, [
    error,
    onHandled,
    resetCheckout,
    checkoutMode,
    sellTokenChainId,
    buyTokenChainId,
    sellInputFormatted,
    buyInputFormatted,
    sellTokenAddress,
    buyTokenAddress,
    sellTokenSymbol,
    buyTokenSymbol,
  ]);

  return null;
}

class Boundary extends Component<TransactionModuleErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {};

  static getDerivedStateFromError(error: Error) {
    return { error, hasBeenHandled: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (shouldIgnore(error)) return;

    const eventName =
      error instanceof ApiError
        ? 'txn_module_zeroex_api_error'
        : error instanceof JupiterApiError
          ? 'txn_module_jupiter_api_error'
          : 'txn_module_matcha_error';
    reportError(error, eventName, errorInfo);
  }

  handleError = () => {
    this.setState({ hasBeenHandled: true });
    setTimeout(() => this.setState({ error: undefined, hasBeenHandled: false }), 100);
  };

  render() {
    const { error, hasBeenHandled } = this.state;
    if (!error) return this.props.children;

    logger.error(error);

    if (!hasBeenHandled) {
      return <ErrorHandler {...this.props} error={error} onHandled={this.handleError} />;
    }

    return this.props.children;
  }
}

/** Catches transaction-module view errors and delegates reset state to its owner. */
export function TransactionModuleErrorBoundary(props: TransactionModuleErrorBoundaryProps) {
  return <Boundary {...props} />;
}
